/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */
import './BaseModal.scss';

import { noop } from 'lodash';
import { bool, element, func, oneOfType, string } from 'prop-types';
import React, { Fragment } from 'react';
import Helmet from 'react-helmet';

import ClickOffSensor from 'components/shared/ClickOffSensor/ClickOffSensor';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import { contentConverter } from 'services/Content';

const hasEscapeBeenPressed = ({ code, key, keyCode } = {}) => {
  return code === 'Escape' || key === 'Esc' || keyCode == 27;
};

export default class BaseModal extends React.Component {
  static propTypes = {
    flex: bool,
    close: func,
    open: func,
    iconAfter: bool,
    isOpen: bool,
    hideAnchor: bool,
    hideClose: bool,
    hideIcon: bool,
    className: string,
    containerClassName: string,
    linkContent: oneOfType([element, string]),
    modal: element,
    icon: oneOfType([element, string]),
    linkContentClass: string,
    renderTitleContent: oneOfType([element, string]),
    renderModalContent: oneOfType([element, string]),
    title: string,
    disableClickOff: bool,
    disableFixed: bool
  };

  state = {
    showModal: Boolean(this.props.isOpen || this.props.forceOpen),
    hasKeydownListener: false
  };

  componentDidMount() {
    const { showModal } = this.state;
    if (showModal) {
      document.addEventListener('keydown', this.handleKeydownEvent);
      this.setState({
        hasKeydownListener: true
      });
    }
  }

  componentDidUpdate() {
    const { hasKeydownListener, showModal } = this.state;
    if (showModal && !hasKeydownListener) {
      document.addEventListener('keydown', this.handleKeydownEvent);
    }
  }

  handleKeydownEvent = (e) => {
    const { hideClose } = this.props;
    const hasCloseButtonHidden = Boolean(hideClose);
    if (!hasCloseButtonHidden && hasEscapeBeenPressed(e)) {
      this.hideModal(e);
    }
  };

  containerRef = React.createRef();

  showModal = (e) => {
    if (this.props.open) {
      this.props.open(e);
    }
    this.setState({ showModal: true });
    return false; // cancel link follow
  };

  hideModal = (e) => {
    if (this.props.close) {
      this.props.close(e);
    }
    document.removeEventListener('keydown', this.handleKeydownEvent);
    this.setState({ showModal: false });
  };

  containerClassName() {
    return 'modal-info-container ' + (this.props.className || '');
  }

  linkClassName() {
    return '';
  }

  renderIcon() {
    const { hideIcon } = this.props;
    return !hideIcon && <i className="aa-question-circle modal-info-icon" />;
  }

  renderTitleContent() {
    const { title = 'Important Information', isInvestorMag } = this.props;
    return isInvestorMag ? (
      <div
        dangerouslySetInnerHTML={{ __html: contentConverter.makeHtml(title) }}
      />
    ) : (
      title
    );
  }

  renderLinkContent() {
    const { hideAnchor, linkContentClass = '', linkContent = '' } = this.props;
    if (hideAnchor) {
      return '';
    }

    return <div className={linkContentClass}>{linkContent}</div>;
  }

  renderModalContent() {
    if (this.props.children) {
      return this.props.children;
    }
    throw new Error(
      'ModalInfoBase subclass did not override renderModalContent as required.'
    );
  }

  renderModal() {
    const {
      closeTitle = 'Close',
      disableClickOff,
      hideClose,
      isOpen,
      renderModalContent = this.renderModalContent(),
      title,
      disableFixed = false
    } = this.props;

    const renderTitleContent = this.renderTitleContent();

    let bodyAttrs = {
      style: 'position: fixed; left: 0; right: 0;  top: 0; bottom: 0;'
    };

    if (!isOpen || disableFixed) {
      bodyAttrs.style = 'position: relative;';
    }

    return (
      <Fragment>
        <Helmet bodyAttributes={bodyAttrs} />
        <div data-metrics-location="PU" className="modal-info-popup-container">
          <ClickOffSensor
            className="modal-info-popup"
            isOpen={isOpen}
            onClickOut={disableClickOff ? noop : this.hideModal}>
            <div className="modal-info-header-container">
              {!hideClose && (
                <div className="modal-info-popup-close-button-container">
                  <span
                    data-testid="close-btn"
                    className="modal-info-popup-close-button"
                    onClick={this.hideModal}>
                    <Close className="modal-info-popup-close-button-icon" />
                  </span>
                </div>
              )}
              {title !== null && ( // `null` ( and not `undefined` ) makes the intention explicit - just use children
                <div data-testid="modal-title" className="modal-info-heading">
                  {renderTitleContent}
                </div>
              )}
            </div>
            {renderModalContent}
            {!hideClose && (
              <button
                className="modal-info-mobile-close-button"
                data-testid={'modal-info-mobile-close-button'}
                onClick={this.hideModal}>
                {closeTitle}
              </button>
            )}
          </ClickOffSensor>
        </div>
      </Fragment>
    );
  }

  render() {
    const {
      containerClassName = `base-modal-container ${this.containerClassName()}`,
      linkContent = this.renderLinkContent(),
      modal = this.renderModal(),
      linkId = '',
      icon = this.renderIcon(),
      iconAfter = false,
      linkContentClass = this.linkClassName(),
      isOpen,
      flex = false
    } = this.props;

    return (
      <Fragment>
        {!this.props.hideAnchor && (
          <a
            className={`modal-info-link ${flex ? 'flex' : ''}`}
            onClick={this.showModal}
            id={linkId}
            onKeyDown={(e) => {
              if(e.key === 'Enter') {
                const link = document.getElementById(linkId);
                if(link) {
                 link.click()
                }
              }
            }}
            tabIndex="0">
            {!iconAfter && icon}
            <span className={`modal-info-link-text ${linkContentClass}`}>
              {linkContent}
            </span>
            {iconAfter && icon}
          </a>
        )}
        {(isOpen || this.state.showModal) && (
          <div className={containerClassName}>{modal}</div>
        )}
      </Fragment>
    );
  }
}
